import { jsxs, jsx } from "react/jsx-runtime";
import { useTheme, Box, styled } from "@mui/material";
import { CampaignType } from "../../../types/Campaign.js";
import { getImageAssetUrl } from "../../../utils/assetUtils.js";
import { formatMoney } from "../../../utils/stringUtils.js";
const StyledContainer = styled("div", {
  shouldForwardProp: (prop) => !["rewardCampaignType", "clickable"].includes(prop.toString())
})(
  ({ clickable, theme }) => ({
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    columnGap: 7,
    "*": {
      mixBlendMode: "unset !important"
    },
    ".reward-pill": {
      textWrap: "nowrap",
      borderRadius: 25,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0,
      display: "flex",
      alignItems: "center",
      ":hover": {
        cursor: clickable ? "pointer" : "default"
      }
    },
    ".rewardPercent-text": {
      color: theme.vars.palette.text.primary,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.24,
      whiteSpace: "nowrap",
      // added for mobile view
      textOverflow: "ellipsis",
      overflow: "hidden"
    }
  })
);
const getConfigByRewardCampaignType = (rewardCampaignType, theme) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  switch (rewardCampaignType) {
    case CampaignType.DailyDealsRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl("instant-reward-icon.webp"),
          alt: "instant-reward-icon"
        },
        rewardTypeDisplay: "instant",
        containerStyle: {
          color: (_a = theme.vars.globalComponentPresets) == null ? void 0 : _a.pill.dailyDeal.contrastText,
          backgroundColor: (_b = theme.vars.globalComponentPresets) == null ? void 0 : _b.pill.dailyDeal.main
        }
      };
    case CampaignType.PurchaseRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl("purchase-reward-icon.webp"),
          alt: "purchase-reward-icon"
        },
        rewardTypeDisplay: "purchase",
        containerStyle: {
          color: (_c = theme.vars.globalComponentPresets) == null ? void 0 : _c.pill.purchase.contrastText,
          backgroundColor: (_d = theme.vars.globalComponentPresets) == null ? void 0 : _d.pill.purchase.main
        }
      };
    case CampaignType.AffiliateRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl("affiliate-reward-icon.webp"),
          alt: "affiliate-reward-icon"
        },
        rewardTypeDisplay: "",
        containerStyle: {
          color: (_e = theme.vars.globalComponentPresets) == null ? void 0 : _e.pill.affiliate.contrastText,
          backgroundColor: (_f = theme.vars.globalComponentPresets) == null ? void 0 : _f.pill.affiliate.main
        }
      };
    case CampaignType.ContentRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl("content-reward-icon.webp"),
          alt: "content-reward-icon"
        },
        rewardTypeDisplay: "",
        containerStyle: {
          color: (_g = theme.vars.globalComponentPresets) == null ? void 0 : _g.pill.content.contrastText,
          backgroundColor: (_h = theme.vars.globalComponentPresets) == null ? void 0 : _h.pill.content.main
        }
      };
    case CampaignType.SurveyRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl("survey-reward-icon.webp"),
          alt: "survey-reward-icon"
        },
        rewardTypeDisplay: "",
        containerStyle: {
          color: (_i = theme.vars.globalComponentPresets) == null ? void 0 : _i.pill.survey.contrastText,
          backgroundColor: (_j = theme.vars.globalComponentPresets) == null ? void 0 : _j.pill.survey.main
        }
      };
    default:
      return {
        iconImgProps: {
          src: void 0,
          alt: "affiliate-reward-icon"
        },
        rewardTypeDisplay: "",
        containerStyle: {
          color: (_k = theme.vars.globalComponentPresets) == null ? void 0 : _k.pill.default.contrastText,
          backgroundColor: (_l = theme.vars.globalComponentPresets) == null ? void 0 : _l.pill.default.main
        }
      };
  }
};
const RewardPillWithText = ({
  rewardAmount,
  rewardCampaignType,
  rewardPercent,
  rewardPercentText,
  rewardPercentTextOverride,
  containerStyle: containerStyleOverride = {},
  pillContainerStyle = {},
  baseFontSize = 14,
  onPillClick,
  className,
  customPillText
}) => {
  const theme = useTheme();
  const { iconImgProps, rewardTypeDisplay, containerStyle } = getConfigByRewardCampaignType(rewardCampaignType, theme);
  const { ...restIconImgProps } = iconImgProps;
  const pillDefaultPadding = {
    paddingTop: `${baseFontSize / 3}px`,
    // top slightly larger than bottom to adjust text alignment
    paddingLeft: `${baseFontSize * 0.95}px`,
    paddingBottom: `${baseFontSize / 3}px`,
    paddingRight: `${baseFontSize * 0.95}px`
  };
  const subfixText = rewardPercentTextOverride ? rewardPercentTextOverride : rewardPercent ? Math.round(rewardPercent) + "% " + (rewardPercentText ?? `${rewardTypeDisplay} reward`) : null;
  const pillText = customPillText ?? formatMoney(rewardAmount);
  return /* @__PURE__ */ jsxs(
    StyledContainer,
    {
      rewardCampaignType,
      style: {
        visibility: rewardAmount || customPillText ? "visible" : "hidden",
        display: "flex",
        ...containerStyleOverride
      },
      clickable: !!onPillClick,
      className,
      children: [
        /* @__PURE__ */ jsx(Box, { sx: { borderRadius: "999px", backgroundColor: "#fff", display: "flex" }, children: /* @__PURE__ */ jsxs(
          Box,
          {
            className: "reward-pill",
            sx: {
              ...containerStyle,
              ...pillDefaultPadding,
              ...pillContainerStyle,
              fontWeight: 600,
              fontFamily: "inherit"
            },
            onClick: onPillClick,
            children: [
              iconImgProps.src && !customPillText ? /* @__PURE__ */ jsx(
                "img",
                {
                  ...restIconImgProps,
                  style: {
                    maxWidth: baseFontSize,
                    maxHeight: baseFontSize,
                    objectFit: "contain",
                    marginRight: baseFontSize * 0.416,
                    verticalAlign: "middle"
                  }
                }
              ) : null,
              /* @__PURE__ */ jsx(
                Box,
                {
                  component: "span",
                  sx: {
                    lineHeight: "1.3em",
                    margin: 0,
                    padding: 0
                  },
                  children: pillText
                }
              )
            ]
          }
        ) }),
        subfixText ? /* @__PURE__ */ jsx(
          "div",
          {
            className: "rewardPercent-text",
            style: {
              fontWeight: 600,
              fontFamily: "inherit"
            },
            children: subfixText
          }
        ) : null
      ]
    }
  );
};
export {
  RewardPillWithText
};
//# sourceMappingURL=RewardPillWithText.js.map
